import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Worker } from '@react-pdf-viewer/core';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect } from 'react';
import {
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useCustomStore } from '../../../hooks';
import { SnackBarConfig } from '../../../utils/SnackBarConfig';
import ComingSoon from '../ComingSoon';
import DocumentsFilter from './documentsFilter';
import DocumentsList from './documentsLists';
import useStyles from './documentsStyle';

type DocumentsProps = {
  headingName?: string;
  backButton?: boolean | any;
  backUrl?: string;
  type?: string;
};
type FiltersTypes = {
  tags: Array<string>;
  fileName: string;
  driverName: string;
  vinNumber: string;
  driver?: { id: string; name: string };
  unit?: { id: string; vim: string };
};
const Documents: React.FC<DocumentsProps> = (props: DocumentsProps) => {
  const { backUrl = '/', backButton = false, type: routeType = '' } = props;
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const { documentsStore } = useCustomStore();
  const { enqueueSnackbar } = useSnackbar();
  const useNavigator = useNavigate();
  const navType = useNavigationType();

  const [show] = React.useState<boolean>(false);

  const [filters, setFilters] = React.useState<FiltersTypes>({
    driver: undefined,
    driverName: '',
    fileName: '',
    tags: [],
    unit: undefined,
    vinNumber: '',
  });

  const BackToPage = () => {
    useNavigator(backUrl);
  };
  const setAnchorEl = (ele: any) => {
    const hasAppliedFilter =
      documentsStore.Filters.fileName ||
      documentsStore.Filters.tags.length ||
      documentsStore.Filters.driverName ||
      documentsStore.Filters.vinNumber ||
      documentsStore.Filters.driver ||
      documentsStore.Filters.unit;
    const hasNewFilter =
      filters.fileName ||
      filters.driverName ||
      filters.tags.length ||
      filters.vinNumber;
    if (ele === 'reset' && hasAppliedFilter) {
      setFilters((pre) => ({
        ...pre,
        driver: undefined,
        driverName: '',
        fileName: '',
        tags: [],
        unit: undefined,
        vinNumber: '',
      }));
      documentsStore.resetFilters();
      getDocumentList();
    } else if (ele === 'apply' && hasNewFilter) {
      documentsStore.setFilters({
        ...documentsStore.Filters,
        ...filters,
        nextLink: '',
      });
      getDocumentList();
    }
    documentsStore.setAnchorEl(null);
    documentsStore.setFilterShowOrHide(false);
  };

  const getDocumentListInitial = useCallback(async () => {
    documentsStore.resetDocumentsList();
    documentsStore.setFilters({
      ...documentsStore.Filters,
      nextLink: '',
      sortBy: 'fileName',
      sortDirection: 'asc',
    });
    const cameFromBack = navType == 'POP';
    if (!cameFromBack) {
      documentsStore.resetFilters();
    }
    const documentResp = await documentsStore.getDocuments({
      docType: documentsStore.ApiCallType.docType,
      id: documentsStore.ApiCallType.id,
    });
    if (documentResp.isErr()) {
      enqueueSnackbar(String(documentResp.error.message), SnackBarConfig('e'));
      documentsStore.setDocumentResponse([]);
    }
  }, [documentsStore, navType, enqueueSnackbar]);
  const getDocumentList = useCallback(async () => {
    documentsStore.resetDocumentsList();
    const documentResp = await documentsStore.getDocuments({
      docType: documentsStore.ApiCallType.docType,
      id: documentsStore.ApiCallType.id,
    });
    if (documentResp.isErr()) {
      enqueueSnackbar(String(documentResp.error.message), SnackBarConfig('e'));
      documentsStore.setDocumentResponse([]);
    }
  }, [documentsStore, enqueueSnackbar]);

  const sortBY = (e: string) => {
    documentsStore.setFilters({
      ...documentsStore.Filters,
      ...filters,
      nextLink: '',
      sortBy: e,
      sortDirection:
        documentsStore.Filters.sortDirection === 'desc' ? 'asc' : 'desc',
    });
    getDocumentList();
  };
  const removeFilter = (key: string, value: string) => {
    const arr = [...documentsStore.Filters.tags];
    if (key === 'tags') {
      arr.splice(Number(value), 1);
    }
    documentsStore.setFilters({
      ...documentsStore.Filters,
      ...{ [key]: key === 'tags' ? arr : value },
      ...{ nextLink: '' },
      driver: key === 'driver' ? undefined : documentsStore.Filters.driver,
      driverName: key === 'driverName' ? '' : documentsStore.Filters.driverName,
      fileName: key === 'fileName' ? '' : documentsStore.Filters.fileName,
      unit: key === 'unit' ? undefined : documentsStore.Filters.unit,
      vinNumber: key === 'vinNumber' ? '' : documentsStore.Filters.vinNumber,
    });
    setFilters((pre) => ({
      ...pre,
      ...{ [key]: key === 'tags' ? arr : value },
      driver: key === 'driver' ? undefined : pre.driver,
      driverName: key === 'driverName' ? '' : pre.driverName,
      fileName: key === 'fileName' ? '' : pre.fileName,
      unit: key === 'unit' ? undefined : pre.unit,
      vinNumber: key === 'vinNumber' ? '' : pre.vinNumber,
    }));
    getDocumentList();
  };
  useEffect(() => {
    documentsStore.setApiCallType({
      docType: routeType,
      id: id ? id : '',
    });
    getDocumentListInitial();
  }, [documentsStore, getDocumentListInitial, id, routeType]);

  useEffect(() => {
    documentsStore.setHeaderName(searchParams && searchParams.get('name'));
  }, [documentsStore, searchParams]);

  if (show) {
    return <ComingSoon />;
  }
  return (
    <Grid container>
      <Grid item md={12}>
        <Typography component={'div'} className={classes.filterList}>
          {documentsStore.Filters.tags && documentsStore.Filters.tags.length
            ? documentsStore.Filters.tags.map((val: any, index: number) => (
                <Typography
                  component={'div'}
                  className={classes.filterItem}
                  key={index}
                  onClick={() => removeFilter('tags', String(index))}>
                  <Typography component={'div'} className={classes.filterText}>
                    {val}
                  </Typography>
                  <Typography className={classes.close} component={'span'}>
                    X
                  </Typography>
                </Typography>
              ))
            : ''}
          {documentsStore.Filters.fileName ? (
            <Typography
              component={'div'}
              className={classes.filterItem}
              onClick={() => removeFilter('fileName', '')}>
              <Typography component={'div'} className={classes.filterText}>
                {documentsStore.Filters.fileName}
              </Typography>
              <Typography className={classes.close} component={'span'}>
                X
              </Typography>
            </Typography>
          ) : (
            ''
          )}
          {documentsStore.Filters.driverName ? (
            <Typography
              component={'div'}
              className={classes.filterItem}
              onClick={() => removeFilter('driverName', '')}>
              <Typography component={'div'} className={classes.filterText}>
                {documentsStore.Filters.driverName}
              </Typography>
              <Typography className={classes.close} component={'span'}>
                X
              </Typography>
            </Typography>
          ) : (
            ''
          )}
          {documentsStore.Filters.vinNumber ? (
            <Typography
              component={'div'}
              className={classes.filterItem}
              onClick={() => removeFilter('vinNumber', '')}>
              <Typography component={'div'} className={classes.filterText}>
                {documentsStore.Filters.vinNumber}
              </Typography>
              <Typography className={classes.close} component={'span'}>
                X
              </Typography>
            </Typography>
          ) : (
            ''
          )}
          {documentsStore.Filters.driver ? (
            <Typography
              component={'div'}
              className={classes.filterItem}
              onClick={() => removeFilter('driver', '')}>
              <Typography component={'div'} className={classes.filterText}>
                {documentsStore.Filters.driver?.name}
              </Typography>
              <Typography className={classes.close} component={'span'}>
                X
              </Typography>
            </Typography>
          ) : (
            ''
          )}
          {documentsStore.Filters.unit ? (
            <Typography
              component={'div'}
              className={classes.filterItem}
              onClick={() => removeFilter('unit', '')}>
              <Typography component={'div'} className={classes.filterText}>
                {documentsStore.Filters.unit?.vim}
              </Typography>
              <Typography className={classes.close} component={'span'}>
                X
              </Typography>
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <Typography
          sx={{
            alignItems: 'center',
            display: 'flex',
            letterSpacing: 0,
            lineHeight: '19px',
            padding: '0rem 2rem 0.5rem 2rem',
          }}>
          {backButton ? (
            <div
              style={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                paddingRight: '8px',
              }}
              onClick={BackToPage}>
              <ArrowBackIcon fontSize={'small'} />
              <Typography
                sx={{
                  color: '#241A2E',
                  fontFamily: 'FiraSans-Regular',
                  fontSize: '14px',
                }}>
                {'Back'}
              </Typography>
            </div>
          ) : (
            ''
          )}
          <Typography
            sx={{
              color: '#241A2E',
              fontSize: '16px',
              textTransform: 'capitalize',
            }}>
            {documentsStore.DocumentsTotalCount} Documents
          </Typography>
        </Typography>
        <Typography component={'div'} style={{ padding: '0 2rem' }}>
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
            <DocumentsList
              {...{
                HasMoreResults: documentsStore.HasMoreResults,
                enqueueSnackbar,
                records: documentsStore.DocumentsList
                  ? documentsStore.DocumentsList
                  : null,
                sortBY,
                storeFilter: documentsStore.Filters,
              }}
            />
          </Worker>
        </Typography>
      </Grid>
      <DocumentsFilter
        {...{
          anchorEl: documentsStore.AnchorEl,
          canSearchDriver: documentsStore.ApiCallType.docType !== 'driver',
          canSearchUnit: documentsStore.ApiCallType.docType !== 'unit',
          filters,
          open: documentsStore.ShowFilter,
          setAnchorEl,
          setFilters,
          storeFilter: documentsStore.Filters,
        }}
      />
    </Grid>
  );
};

export default observer(Documents);
